import React from 'react';

const UNIT_TO_MS = {
  seconds: 1000,
  minutes: 60 * 1000,
  hours:   60 * 60 * 1000,
  days:    24 * 60 * 60 * 1000,
  weeks:   7 * 24 * 60 * 60 * 1000,
  months:  30 * 24 * 60 * 60 * 1000,
};

/**
 * EdgeDurationFilterControl - Control for edge duration filters
 *
 * Filters are added via graph interaction. Each filter is editable in-place:
 * operator (≤/≥), mode (include/exclude), threshold value, and unit.
 *
 * @param {Array} value - Current edge duration filters
 * @param {Function} onChange - Callback when filters change
 * @param {boolean} disabled - Whether control is disabled
 * @param {Function} onAddEdge - Callback to trigger graph edge selection
 */
const EdgeDurationFilterControl = ({ value = [], onChange, disabled = false, onAddEdge }) => {
  const updateFilter = (index, updates) => {
    if (disabled) return;
    const updated = value.map((f, i) => {
      if (i !== index) return f;
      const next = { ...f, ...updates };
      // Recompute thresholdMs whenever value or unit changes
      const numVal = parseFloat(next.value);
      next.thresholdMs = !isNaN(numVal) ? numVal * (UNIT_TO_MS[next.unit] ?? 1000) : f.thresholdMs;
      return next;
    });
    onChange(updated);
  };

  const removeFilter = (index) => {
    if (disabled) return;
    onChange(value.filter((_, i) => i !== index));
  };

  const formatEdgeLabel = (filter) => filter.edgeLabel || filter.edgeId || 'Unknown Edge';

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Edge Duration Filters
        {value.length > 0 && (
          <span className="ml-2 text-xs text-gray-500">({value.length} filter{value.length !== 1 ? 's' : ''})</span>
        )}
      </label>

      {onAddEdge && (
        <button
          type="button"
          onClick={onAddEdge}
          disabled={disabled}
          className="w-full px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          + Add Edge Filter from Graph
        </button>
      )}

      {value.length === 0 ? (
        <div className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded border border-gray-200">
          No edge duration filters configured.
          <br />
          Click the button above to select an edge from the graph.
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((filter, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200 space-y-2">
              {/* Header: edge label + remove button */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">
                  {formatEdgeLabel(filter)}
                </span>
                <button
                  type="button"
                  onClick={() => removeFilter(index)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed flex-shrink-0"
                  title="Remove filter"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Operator + Mode */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Operator</label>
                  <select
                    value={filter.operator || 'le'}
                    onChange={(e) => updateFilter(index, { operator: e.target.value })}
                    disabled={disabled}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="le">≤ (at most)</option>
                    <option value="ge">≥ (at least)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mode</label>
                  <select
                    value={filter.mode || 'include'}
                    onChange={(e) => updateFilter(index, { mode: e.target.value })}
                    disabled={disabled}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="include">Include</option>
                    <option value="exclude">Exclude</option>
                  </select>
                </div>
              </div>

              {/* Value + Unit */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Threshold</label>
                  <input
                    type="number"
                    min="0"
                    value={filter.value ?? ''}
                    onChange={(e) => updateFilter(index, { value: e.target.value })}
                    disabled={disabled}
                    placeholder="e.g. 5"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Unit</label>
                  <select
                    value={filter.unit || 'days'}
                    onChange={(e) => updateFilter(index, { unit: e.target.value })}
                    disabled={disabled}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="seconds">seconds</option>
                    <option value="minutes">minutes</option>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                    <option value="weeks">weeks</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EdgeDurationFilterControl;
